import { Schema } from "effect"
import { EntityRef, Evidence, Hold, Reversibility, Risk, Taint } from "../common"
import { IntakeKind, IntakeSource } from "../domain/intake"
import { MemoryDraft, MemoryScope } from "../domain/knowledge"
import { Capability, Mind, PolicyDocument } from "../domain/org"
import { BetVerdictValue, KpiSample } from "../domain/plan"
import { ProposalBody, ProposalState, QuestionOption } from "../domain/proposal"
import { ArtifactKind } from "../domain/task"
import { Member, RepoBaseBranch, WorkerSettings } from "../domain/workspace"
import {
  AgentId,
  AreaId,
  ArtifactId,
  ChangesetId,
  CommandId,
  CommentId,
  Epoch,
  Handle,
  InitiativeId,
  IntakeId,
  IntegrationId,
  KpiId,
  Markdown,
  MemoryItemId,
  Millis,
  PolicyId,
  ProjectId,
  ProposalId,
  RepoId,
  RunId,
  Seq,
  Sha256,
  Slug,
  TaskId,
  UserId,
  WakeId,
  WorkspaceId,
} from "../ids"
import { Unmet } from "../placement"
import { Executor, RunError, RunKind, RunOutcome, SessionRef, StopReason, Usage, Verdict } from "../run"
import { FlowCommand, GraphCommand } from "./graph"

// #region envelope
const opt = Schema.optionalKey

/** Each state-owning DO has its own log; a seq is only meaningful together with its log. */
export const LogId = Schema.TaggedUnion({
  project: { projectId: ProjectId },
  workspace: { workspaceId: WorkspaceId },
  agent: { agentId: AgentId },
  user: { userId: UserId },
})
export const EventRef = Schema.Struct({ log: LogId, seq: Seq })
export type EventRef = typeof EventRef.Type

/** What every writer sends. There is no `actor` field: the server stamps it from the authenticated credential. */
export const CommandEnvelope = Schema.Struct({
  commandId: CommandId, // client-minted; the default idempotency key
  /** Stable key when one logical intent may be re-issued with new commandIds (agent retries). */
  idempotencyKey: opt(Schema.NonEmptyString),
  /** Last seq the issuer had applied. A rebase hint for PreconditionFailed, never a blanket rejection. */
  baseSeq: Seq,
  /** Optimistic preconditions on exactly the records this command depends on. */
  expect: opt(Schema.Array(Schema.Struct({ ref: EntityRef, lastSeq: Seq }))),
  /** Direct cause: a command, or an event in any log (e.g. the wake that started this turn). */
  causedBy: opt(Schema.Union([CommandId, EventRef])),
  /** Further causes, e.g. the ten coalesced task completions behind one lead turn. */
  links: opt(Schema.Array(EventRef)),
  /** Inherited; minted only at true roots (human input, schedule tick, webhook). Also seeds the OTel trace id. */
  correlationId: opt(Schema.String),
  command: Schema.suspend(() => Command),
})
// #endregion envelope

// #region commands
/** Needs-you actions on one proposal: Approve · Edit (approve_edited) · Reply (answer) · Reject; Defer is client-side. */
export const ProposalDecision = Schema.TaggedUnion({
  approve: {
    selection: opt(Schema.Array(Schema.Int)), // plan_change: accept per change (dependency-closed)
    /** Permission proposals only (settings wave §4.5): "run" = allow this tool for the rest of the run. */
    remember: opt(Schema.Literals(["once", "run"])),
  },
  /** People only: approve a changed body of the same kind ("remember that…": confirm or fix scope and type). Written
   *  as ProposalRevised (by the approver) + ProposalApproved at the new revision, in one transaction. */
  approve_edited: { body: ProposalBody },
  reject: { reason: opt(Schema.String) },
  request_revision: { note: Schema.NonEmptyString },
  answer: { optionKey: Schema.NullOr(Schema.String), text: Schema.NullOr(Schema.String) }, // question
  adopt: {}, // lead adopts a Mayor suggestion: re-filed as the lead's own SubmitChangeset under its policy
})
export type ProposalDecision = typeof ProposalDecision.Type

/** Project DO commands that are not graph edits. Graph edits only travel inside SubmitChangeset. */
export const ProjectCommand = Schema.TaggedUnion({
  // ---- plan (bumps the plan version) ----
  /** apply_allowed (default): apply the dependency-closed allowed subset now, propose the rest as ONE proposal.
   *  propose_all: any "propose" verdict turns the whole set into one proposal (atomic review). */
  SubmitChangeset: {
    changesetId: ChangesetId,
    rationale: Markdown,
    changes: Schema.NonEmptyArray(GraphCommand),
    onPropose: Schema.Literals(["apply_allowed", "propose_all"]),
  },
  UndoChangeset: { changesetId: ChangesetId, target: ChangesetId }, // forward-only inverse
  RevertPlan: { changesetId: ChangesetId, toVersion: Schema.Int, rationale: Markdown }, // forward-only diff back to v
  // ---- ledger ----
  OpenProposal: {
    proposalId: ProposalId,
    body: ProposalBody,
    reasoning: Markdown,
    oneLine: Schema.String,
    risk: opt(Risk),
    reversibility: opt(Reversibility),
    blocks: Schema.Array(EntityRef),
    decideBy: opt(Millis),
    intentKey: Schema.String,
  },
  ReviseProposal: { proposalId: ProposalId, revision: Schema.Int, body: ProposalBody, reasoning: Markdown },
  WithdrawProposal: { proposalId: ProposalId },
  DecideProposal: {
    proposalId: ProposalId,
    revision: Schema.Int, // must equal the current revision
    contentHash: Sha256, // must equal the current hash: you approve exactly what you saw
    decision: ProposalDecision,
  },
  ExpireProposal: { proposalId: ProposalId },
  // ---- steering (people; owner/admin-gated; not plan edits) ----
  SetHold: { ref: EntityRef, hold: Hold },
  UpdateProject: {
    patch: Schema.Struct({
      name: opt(Schema.String),
      description: opt(Markdown),
      status: opt(Schema.Literals(["active", "paused", "archived"])),
      repos: opt(Schema.Array(RepoId)),
      wip: opt(Schema.Struct({ maxActiveInitiatives: Schema.Int })),
      /** Settings wave: the project's worker presets (saved whole). */
      workers: opt(WorkerSettings),
      /** Settings wave: per-repo base branches (the registry default = no entry). */
      baseBranches: opt(Schema.Array(RepoBaseBranch)),
    }),
  },
  CreatePolicy: {
    policyId: PolicyId,
    subject: Schema.TaggedUnion({ project: {}, area: { areaId: AreaId }, agent: { agentId: AgentId } }),
    document: PolicyDocument,
  },
  RevisePolicy: {
    policyId: PolicyId,
    revision: Schema.Int,
    document: PolicyDocument,
    restoredFrom: opt(Schema.Int),
  },
  /** system:upgrade only (decision 0032). For every policy that mentions `like` and not `add`, copy `like`'s rules to `add`. */
  UpgradePolicies: { add: Capability, like: Capability },
  GrantIntegration: {
    areaId: AreaId,
    grant: Schema.Struct({
      integrationId: IntegrationId,
      read: Schema.Array(Schema.String),
      write: Schema.Array(Schema.String),
      outward: Schema.Array(Schema.String),
    }),
  },
  RevokeIntegration: { areaId: AreaId, integrationId: IntegrationId },
  // ---- runs ----
  /** Issued by the DO's effect layer after assembling the context pack (scheduler reaction, review stage, retry,
   *  approved publish, or an Agent DO turn request). decide validates and dedupes on the run's idempotency key. */
  RequestRun: {
    runId: RunId,
    kind: RunKind,
    taskId: opt(TaskId),
    subjectRunId: opt(RunId),
    publish: opt(Schema.Struct({ proposalId: ProposalId, revision: Schema.Int })),
    agentId: opt(AgentId),
    wakeBatchId: opt(WakeId),
    context: Schema.Struct({ hash: Sha256, manifestRef: Schema.String, taint: Taint }),
    executor: opt(Executor), // platform turns and gateway publish runs; runner runs get theirs at lease time
    /** Workers and validators: the job's worktree branch (null = scratch). Display only; copied to RunRequested. */
    branch: opt(Schema.NullOr(Schema.String)),
  },
  CancelRun: { runId: RunId, reason: Schema.String },
  RecordRunWaiting: { runId: RunId, unmet: Schema.Array(Unmet), since: Millis }, // hub; only when the reasons change
  RecordRunLeased: { runId: RunId, epoch: Epoch, executor: Executor }, // hub (phase: preparing)
  RecordRunStarted: { runId: RunId, epoch: Epoch, sessionIn: Schema.NullOr(SessionRef) }, // hub / Agent DO (phase: running)
  RecordRunMilestone: {
    runId: RunId,
    epoch: Epoch,
    kind: Schema.Literals([
      "pr_opened",
      "tests_passed",
      "tests_failed",
      "checkpoint",
      "awaiting_permission",
      "note",
    ]),
    text: Schema.String,
    ref: Schema.NullOr(Schema.String),
  },
  ReportOutcome: { outcome: RunOutcome }, // MCP report_outcome; run and epoch come from the token
  ReportVerdict: { verdict: Verdict }, // MCP report_verdict (validators)
  RaiseRunTaint: { runId: RunId, epoch: Epoch, source: Schema.String }, // MCP endpoint / gateway saw an untrusted read
  RecordRunEnded: {
    runId: RunId,
    epoch: Epoch,
    stopReason: StopReason,
    usage: Schema.Array(Usage),
    error: Schema.NullOr(RunError),
    sessionOut: Schema.NullOr(SessionRef),
    transcriptRef: Schema.NullOr(Schema.String),
  },
  // ---- review ----
  RecordReview: {
    taskId: TaskId,
    round: Schema.Int,
    verdict: Schema.Literals(["approve", "changes_requested"]),
    notes: Markdown,
    findings: Schema.Array(Schema.String),
  },
  // ---- comments and the agent bus ----
  PostComment: {
    commentId: CommentId,
    target: EntityRef,
    threadId: Schema.NullOr(CommentId),
    body: Markdown,
  },
  EditComment: { commentId: CommentId, body: Markdown },
  ResolveThread: { threadId: CommentId },
  /** `to` is resolved by decide: exactly one level up (worker → task assignee; role agent → lead). */
  Ask: { commentId: CommentId, target: EntityRef, body: Markdown },
  Answer: { commentId: CommentId, askId: CommentId, body: Markdown },
  // ---- artifacts ----
  PostArtifact: {
    artifactId: ArtifactId,
    taskId: Schema.NullOr(TaskId),
    kind: ArtifactKind,
    title: Schema.String,
    uri: Schema.String,
    contentType: Schema.NullOr(Schema.String),
    sizeBytes: Schema.NullOr(Schema.Int),
    sha256: Schema.NullOr(Sha256),
    preview: Schema.NullOr(Schema.String),
    interfaceKey: Schema.NullOr(Slug),
    supersedes: Schema.NullOr(ArtifactId),
  },
  SetArtifactState: {
    artifactId: ArtifactId,
    state: Schema.Literals(["draft", "final", "published", "rejected"]),
  },
  // ---- memory (project, area, task scopes here; others applied cross-DO) ----
  ProposeMemory: {
    proposalId: ProposalId,
    op: Schema.Literals(["create", "revise", "archive", "promote"]),
    itemId: MemoryItemId,
    draft: Schema.NullOr(MemoryDraft),
    fromScope: Schema.NullOr(MemoryScope),
    evidence: Schema.Array(Evidence),
    reasoning: Markdown,
  },
  WriteMemory: {
    itemId: MemoryItemId,
    op: Schema.Literals(["create", "revise", "archive", "revoke"]),
    draft: Schema.NullOr(MemoryDraft),
    reason: Schema.NullOr(Schema.String),
  }, // people only
  // ---- outward actions (Phase 2) ----
  RequestOutwardAction: {
    proposalId: ProposalId,
    taskId: TaskId,
    integrationId: IntegrationId,
    tool: Schema.String,
    args: Schema.Json,
    preview: Schema.Struct({
      summary: Schema.String,
      recipient: Schema.NullOr(Schema.String),
      amount: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Markdown),
      diffArtifactId: Schema.NullOr(ArtifactId),
    }),
  },
  RecordOutwardResult: {
    proposalId: ProposalId,
    revision: Schema.Int,
    runId: RunId,
    result: Schema.TaggedUnion({ ok: { resultRef: Schema.String }, failed: { error: Schema.String } }),
  },
  // ---- intake (Phase 2; decision 0002) ----
  /** People (web, CLI, external MCP) and agents (a routine's turn) capture; nobody's capture creates work. */
  CaptureIntake: {
    intakeId: IntakeId,
    kind: IntakeKind,
    title: Schema.String,
    body: Markdown,
    attachments: Schema.Array(ArtifactId),
    source: IntakeSource,
    suggestedAreaId: opt(AreaId),
  },
  /** People and the lead. */
  TriageIntake: { intakeId: IntakeId, suggestedAreaId: Schema.NullOr(AreaId) },
  LinkIntake: { intakeId: IntakeId, ref: EntityRef },
  CloseIntake: {
    intakeId: IntakeId,
    as: Schema.TaggedUnion({ duplicate: { of: IntakeId }, closed: { reason: Schema.String } }),
  },
  // ---- KPIs and outcomes ----
  RecordKpiSample: { kpiId: KpiId, sample: KpiSample },
  RecordBetVerdict: {
    initiativeId: InitiativeId,
    verdict: BetVerdictValue,
    evidence: Schema.Array(Evidence),
  }, // Phase 3, evaluator agent
  // ---- watchdog ----
  EscalateStall: {
    proposalId: ProposalId,
    subject: EntityRef,
    signal: Schema.String,
    options: Schema.Array(QuestionOption),
  },
  // ---- sync and genesis ----
  SyncWorkspace: {
    members: Schema.Array(Member),
    mayor: Schema.NullOr(Schema.Struct({ agentId: AgentId, ownerId: UserId })),
  },
  CreateProject: {
    projectId: ProjectId,
    workspaceId: WorkspaceId,
    slug: Slug,
    name: Schema.String,
    description: Markdown,
    repos: Schema.Array(RepoId),
    policyId: PolicyId,
    lead: Schema.Struct({
      agentId: AgentId,
      name: Schema.String,
      handle: Handle,
      mind: Mind,
      policyId: PolicyId,
    }),
  },
})

/** Every command the Project DO accepts. FlowCommand members are also valid as question-option effects. */
export const Command = Schema.Union([ProjectCommand, FlowCommand])
export type Command = typeof Command.Type
// #endregion commands

// #region results
export const CommandRejection = Schema.TaggedUnion({
  /** Schema or planner-validator failure. Messages are written so an agent can fix and retry. */
  Invalid: {
    issues: Schema.Array(
      Schema.Struct({ path: Schema.String, message: Schema.String, changeIndex: Schema.NullOr(Schema.Int) }),
    ),
  },
  Forbidden: {
    reason: Schema.Literals([
      "actor_kind",
      "not_owner",
      "policy_never",
      "held",
      "system_cannot_create",
      "self_approval",
      "frozen_contract",
    ]),
    detail: Schema.String,
  },
  PreconditionFailed: { ref: EntityRef, expected: Seq, actual: Seq },
  /** Meaning-based dedupe hit (task intent, open proposal, run idempotency key). Points at the existing record. */
  Duplicate: { existing: EntityRef, reason: Schema.String },
  WipLimit: {
    scope: Schema.TaggedUnion({ area: { areaId: AreaId }, project: {} }),
    active: Schema.Int,
    limit: Schema.Int,
  },
  StaleLease: { runId: RunId, presented: Epoch, current: Epoch, ended: Schema.Boolean },
  StaleProposal: { proposalId: ProposalId, currentRevision: Schema.Int, state: ProposalState },
  NotFound: { ref: EntityRef },
  Conflict: { reason: Schema.String }, // e.g. idempotency key reused with a different body
})
export type CommandRejection = typeof CommandRejection.Type

const Applied = Schema.TaggedStruct("Applied", {
  firstSeq: Seq,
  lastSeq: Seq,
  planVersion: Schema.NullOr(Schema.Int),
})
const Proposed = Schema.TaggedStruct("Proposed", {
  proposalId: ProposalId,
  seq: Seq,
  /** apply_allowed: what was applied right away alongside the proposal. */
  applied: Schema.NullOr(Schema.Struct({ firstSeq: Seq, lastSeq: Seq, planVersion: Schema.Int })),
})
export const CommandResult = Schema.Union([
  Applied,
  Proposed,
  Schema.TaggedStruct("Deduplicated", { original: Schema.Union([Applied, Proposed]) }),
  Schema.TaggedStruct("Rejected", { rejection: CommandRejection }), // rejections write NO event
]).pipe(Schema.toTaggedUnion("_tag"))
export type CommandResult = typeof CommandResult.Type
// #endregion results
